import { createContext, useContext, useState, useEffect } from 'react';
import { useEloApi, ApiError } from '@/api/api';
import { useAuth0 } from '@auth0/auth0-react';
import { useError } from '@/contexts/ErrorContext';
import { useTranslation } from 'react-i18next';
import { User } from '@/api/users';

const coverImgURL = `https://ia801307.us.archive.org/1/items/instagram-plain-round/instagram%20dip%20in%20hair.jpg`;

interface ChatWootoOnextType {
  isChatwootVisible: boolean;
  toggleChatwootVisibility: (visible: boolean) => void;
}

const ChatwootContext = createContext<ChatWootoOnextType>({
  isChatwootVisible: true,
  toggleChatwootVisibility: (visible: boolean) => {},
});
export const useChatwoot = () => useContext(ChatwootContext);

declare const window: any;

export const ChatwootProvider = ({ children }: { children: React.ReactNode }) => {
  const [isChatwootVisible, setIsChatwootVisible] = useState(false);
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();
  if (!getAccessTokenSilently) return null;
  const api = useEloApi({ getAccessTokenSilently });
  const [user, setUser] = useState<User | undefined>(undefined);
  const [identifyHash, setIdentifyHash] = useState<string | undefined>(undefined);

  const errorContext = useError();
  const { i18n } = useTranslation();

  const CHATWOOT_SCRIPT_ID = 'chatwoot-script';

  const getIdentityHash = async () => {
    try {
      const res = await api.getChatwootToken();
      setIdentifyHash(res.identifier);
      setUser(res.user);
    } catch (error) {
      let errorMessage;
      if (error instanceof ApiError) {
        errorMessage = error.message;
      }
      errorContext.setError(errorMessage);
      console.error(error);
    }
  };

  const loadChatwootScript = (localUser: User, localIdentifyHash: string, lan: string) => {
    const existingScript = document.getElementById(CHATWOOT_SCRIPT_ID);

    if (!existingScript) {
      const BASE_URL = 'https://app.chatwoot.com';
      const script = document.createElement('script');
      script.id = CHATWOOT_SCRIPT_ID;
      script.src = `${BASE_URL}/packs/js/sdk.js`;
      script.defer = true;
      script.async = true;

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        return;
      }

      script.onload = () => {
        window.chatwootSDK.run({
          websiteToken: import.meta.env.VITE_CHATWOOT_KEY,
          baseUrl: BASE_URL,
        });
        window.chatwootSettings = {
          darkMode: 'auto',
        };
        window.$chatwoot.setLocale(lan);
        window.$chatwoot.setUser(localUser.userId, {
          email: localUser?.email || 'default@email.com',
          name: localUser?.name || 'DefaultName',
          avatar_url: localUser?.picture || coverImgURL,
          identifier_hash: localIdentifyHash,
        });
      };

      script.onerror = () => {
        console.error('Error al cargar el SDK de Chatwoot');
      };
    } else {
      if (window?.$chatwoot) {
        window.$chatwoot.setLocale(lan);
        window.$chatwoot.setUser(localUser.userId, {
          email: localUser?.email || 'default@email.com',
          name: localUser?.name || 'DefaultName',
          avatar_url: localUser?.picture || coverImgURL,
          identifier_hash: localIdentifyHash,
        });
      }
    }
  };

  const toggleChatwootVisibility = (visible: boolean) => {
    setIsChatwootVisible(visible);
  };

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      getIdentityHash();
    }
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (user && identifyHash && !!user?.userId && i18n.language) {
      loadChatwootScript(user, identifyHash, i18n.language);
    }
  }, [user, identifyHash, i18n.language]);

  useEffect(() => {
    if (window?.$chatwoot) {
      if (isChatwootVisible) {
        window.$chatwoot.toggleBubbleVisibility('show');
      } else {
        window.$chatwoot.toggleBubbleVisibility('hide');
      }
    }
  }, [isChatwootVisible]);

  return (
    <ChatwootContext.Provider value={{ isChatwootVisible, toggleChatwootVisibility }}>
      {children}
    </ChatwootContext.Provider>
  );
};
